import { useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import type { SelectionExtension } from '@atlaskit/editor-plugin-selection-extension';
import PlusSquareIcon from '@atlaskit/icon/core/plus-square';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useIssueCreateSidePanelContext } from '@confluence/issue-create-side-panel';
import { useContentType, usePageContentId } from '@confluence/page-context';
import { useRightSidebarContext } from '@confluence/page-layout-context';
import type { PageMode } from '@confluence/issue-create-side-panel';

import { messages } from '../messages';
import { jiraWorkItemCreateActions } from '../jiraWorkCreateActions';

export type UseJiraWorkCreateSelectionExtensionProps = {
	isSidePanelOpenRef: React.MutableRefObject<boolean>;
	pageModeRef: React.MutableRefObject<PageMode>;
};

const IssueCreationType = {
	SINGLE_ISSUE: 'SINGLE_ISSUE',
	MULTIPLE_ISSUE: 'MULTIPLE_ISSUE',
	AI_ISSUE: 'AI_ISSUE',
} as const;

type IssueCreationType = (typeof IssueCreationType)[keyof typeof IssueCreationType];

export const useJiraWorkCreateSelectionExtension = ({
	isSidePanelOpenRef,
	pageModeRef,
}: UseJiraWorkCreateSelectionExtensionProps): SelectionExtension => {
	const { formatMessage } = useIntl();
	const [contentId] = usePageContentId();
	const [contentType] = useContentType();
	const rightSidebarContext = useRightSidebarContext();
	const [, actions] = useIssueCreateSidePanelContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fireCreateIssueButtonClickedAnalytics = useCallback(
		(issueCreationType: IssueCreationType, isCreatedFromTable: boolean) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'confluenceHighlightActionsMenu',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'jiraIssueLink',
					type: 'ui',
					attributes: {
						issueCreationType,
						// Set to false because a dropdown or flyout is not implemented at this entry point.
						// TODO: Update this value if a dropdown or flyout is added in the future.
						isDropdownItem: false,
						isCreatedFromTable,
						pageMode: pageModeRef.current,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, pageModeRef],
	);

	const handleSingleCreateClick = useCallback(async () => {
		const activeSelection = window.getSelection();
		const container = document.querySelector('div#ak-editor-textarea');

		fireCreateIssueButtonClickedAnalytics(
			IssueCreationType.SINGLE_ISSUE,
			// Set to false because bulk creation from the table is not yet available at this entry point.
			// TODO: Update this value if bulk creation functionality is added in the future.
			false,
		);

		if (isSidePanelOpenRef.current) {
			const callRightSidebarClose = async () => rightSidebarContext.closeAll();
			// Need to make sure the sidebar is closed before creating a new work item
			await callRightSidebarClose();
		}

		jiraWorkItemCreateActions({
			activeSelection,
			container,
			contentId,
			contentType,
			rightSidebarContext,
			actions,
			pageMode: pageModeRef.current,
		});
	}, [
		fireCreateIssueButtonClickedAnalytics,
		isSidePanelOpenRef,
		contentId,
		contentType,
		rightSidebarContext,
		actions,
		pageModeRef,
	]);

	return {
		name: formatMessage(messages.singleCreateWorkItemName),
		icon: PlusSquareIcon,
		onClick: () => {
			void handleSingleCreateClick();
		},
	};
};
